
import img0 from './pics/CreateBackupJob.png';
import img1 from './pics/CreateBackupJobDetails-Snowflake.png';
import img2 from './pics/ViewBackupJobs.png';
import img3 from './pics/Clickbackupjobdetails-MariaDB.png';
import img4 from './pics/DownloadBackupConfig-Snowflake.png';
import img5 from './pics/DeviceVerification.png';
import img6 from './pics/DeviceVerification2.png';
import img7 from './pics/DeviceConsent.png';
  
const content = `# Snowflake Schema-Only Backup Guide

The objective of the document is to detail how to perform schema-only backup for Snowflake databases.

## Prerequisites

***

### **Operating System**

Supported operating systems:

- \`Microsoft Windows\`
- \`Linux\`

### **PowerShell 7.2 or above**

PowerShell 7.2 or above is required to run the PowerShell script in the following steps. If your system does not have PowerShell installed yet, please refer to the following links:

- [Installing PowerShell on Windows](https://docs.microsoft.com/en-us/powershell/scripting/install/installing-powershell-core-on-windows?view=powershell-7.2)
- [Installing PowerShell on Linux](https://docs.microsoft.com/en-us/powershell/scripting/install/installing-powershell-core-on-linux?view=powershell-7.2)

### **Snowflake Account with Sufficient Permissions**

A Snowflake account with at least the following permissions is required.

**Note**: For Secure Views, only the owner can get the definition.

\`\`\`sql
-- USAGE privilege on warehouse
GRANT USAGE ON WAREHOUSE test_wh TO ROLE SCHEMA_ONLY_BACKUP;
-- USAGE privilege on database
GRANT USAGE ON DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
-- USAGE privilege on all schemas
GRANT USAGE ON ALL SCHEMAS IN DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
GRANT USAGE ON FUTURE SCHEMAS IN DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
-- SELECT, REFERENCES privileges on all tables
GRANT SELECT, REFERENCES ON ALL TABLES IN DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
GRANT SELECT, REFERENCES ON FUTURE TABLES IN DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
-- SELECT, REFERENCES privileges on all external tables
GRANT SELECT, REFERENCES ON ALL EXTERNAL TABLES IN DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
GRANT SELECT, REFERENCES ON FUTURE EXTERNAL TABLES IN DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
-- SELECT, REFERENCES privileges on all views;
GRANT SELECT, REFERENCES ON ALL VIEWS IN DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
GRANT SELECT, REFERENCES ON FUTURE VIEWS IN DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
-- SELECT, REFERENCES privileges on all materialized views
GRANT SELECT, REFERENCES ON ALL MATERIALIZED VIEWS IN DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
GRANT SELECT, REFERENCES ON FUTURE MATERIALIZED VIEWS IN DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
-- USAGE privilege on all PROCEDURES
GRANT USAGE ON ALL PROCEDURES IN DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
GRANT USAGE ON FUTURE PROCEDURES IN DATABASE test_database TO ROLE SCHEMA_ONLY_BACKUP;
\`\`\`

***

## Step 1: Create Backup Job on OnData Portal

To create a backup job, you need an OnData account with a \`Database Administrator\` role or a \`System Administrator\` role. If you do not have such level of access, please contact your system administrator.
  
1. Log in to the OnData Portal. From the main menu, select "Services" -> "Continuity"
  
![Create Backup Job](${ img0 } 'Create Backup Job')

2. Click on "Create Backup Job", enter a Database Name that matches the name of the database that you intend to backup, and provide information on the corresponding Database Engine. **NOTE:** It is important to have the exact database name, database engine and database engine version.
  
![Create Backup Job Input](${ img1 } 'Create Backup Job')

## Step 2: Download Backup Configuration File

1. Select "View Backup Jobs" from the **Data Continuity Dashboard**
  
   ![ConfigFileforUpload](${ img2 } 'ConfigFileforUpload')

2. Find the backup job and then click on arrow on the right to view the details  
  
   ![ConfigFileforUpload](${ img3 } 'ConfigFileforUpload')

3. Click on the "Config File for Upload" link to download the BackupConfig.ini file. It contains configuration information that is unique for handling the backup of this database.
  
   ![ConfigFileforUpload](${ img4 } 'ConfigFileforUpload')

## Step 3: Download the PowerShell Backup Script

Download the appropriate PowerShell script based on your operating system:

- [PowerShell Backup Script for Snowflake on Windows](download_res#BackupSolution/Snowflake/BackupSnowflake-Windows.ps1 'Backup Snowflake')
- [PowerShell Backup Script for Snowflake on Linux](download_res#BackupSolution/Snowflake/BackupSnowflake-Linux.ps1 'Backup Snowflake')

**Note**: It's recommended to keep the file name of **Backup Configuration File** as \`BackupConfig.ini\` and put it in the same folder of **PowerShell Backup Script**. Otherwise, you need to use the \`ConfigPath\` parameter to specify the path to the configuration file.

## Step 4: Install and Configure the ODBC Driver of Snowflake

The **PowerShell Backup Script** downloaded in Step 3 above requires an ODBC Driver in order to connect to Snowflake databases.

Snowflake provides a database driver for connecting to Snowflake using ODBC-based client applications. Please refer to [Snowflake ODBC Driver](https://docs.snowflake.com/en/user-guide/odbc) for more details.

## Step 5: Configure Backup Configuration File

Below is the content of a sample \`BackupConfig.ini\` for a Snowflake database.

\`\`\`ini
[Config]
# DatabaseName: The exact name of the database that you want to backup.
DatabaseName = Sample

# ServerAddress: Specifies the hostname for your account in this format: <account_identifier>.snowflakecomputing.com.
ServerAddress = <account_identifier>.snowflakecomputing.com

# Port: Use the default Port value of 443.
Port = 443

# Username: Specifies the login name of the Snowflake user for authentication.
Username = <Replace with your username>

# Password: Specifies the password of the Snowflake user for authentication.
Password = <Replace with your password>

# OdbcDriver: Specifies the name of the ODBC driver. The default value is: SnowflakeDSIIDriver.
OdbcDriver = SnowflakeDSIIDriver

# Warehouse: Specifies the warehouse to use.
Warehouse = <Replace with your warehouse>
\`\`\`

Description of configuration entries in the \`BackupConfig.ini\` file:

- \`DatabaseName\`: The exact name of the database that you want to backup.
- \`ServerAddress\`: Specifies the hostname for your account in this format: \`<account_identifier>.snowflakecomputing.com\`.
- \`Port\`: Use the default Port value of 443.
- \`Username\`: Specifies the login name of the Snowflake user for authentication.
- \`Password\`: Specifies the password of the Snowflake user for authentication. The script will look for the password of the Snowflake user in the following places sequentially:
  - If a password is specified on the command line with the \`-p '<password>'\` option, the password from the command line will be used.
  - If such option does not exist in the command line, the script will search the \`appsettings.ini\` file for the password.
  - If a password does not exist in the appsettings.ini file either, the script will prompt you to enter the password for the Snowflake user.
- \`OdbcDriver\`: Specifies the name of the ODBC driver. The default value is: **SnowflakeDSIIDriver**.
- \`Warehouse\`: Specifies the warehouse to use.

## Step 6: Run PowerShell Backup Script

1. Run PowerShell Backup Script downloaded in Step 3.

\`\`\`powershell
# Both the \`-ConfigPath\` and \`-p\` switches are optional.

PS /var/opt/backup> /root/scripts/BackupSnowflake-Linux.ps1 -ConfigPath bk_sln/SampleDB.ini -p '<password>'
\`\`\`

2. Authorize the backup request when the script starts to run for the first time. The script will instruct user to open a web browser to authenticate and give consent to the backup script, so that the script is authorized to perform the backup.  

\`\`\`bash
PS /var/opt/backup> ./BackupSnowflake-Linux.ps1 -BackupType s
PowerShell Version is: 7.2
Using config file: /var/opt/backup/BackupConfig.ini
Can not find stored credential, will have to perform Device Code authentication.

To sign in, use a web browser to open the page https://us.ondatahub.com/device and enter the code 491065954 to authenticate.

\`\`\`  

3. Open a web browser (can be on another computer) and navigate to the page https://us.ondatahub.com/device. Enter the code above as the User Code:
  
![DeviceVerification](${ img5 } 'DeviceVerification')

4. You will be prompted to sign into the OnData Portal with your OnData account. Go ahead and sign in.  
  
![DeviceVerification2](${ img6 } 'DeviceVerification2')  

5. Once you sign in, you will be prompted to decide whether you would like to provide the consent to run the script. Select "Yes, Allow" to proceed.
  
![DeviceConsent](${ img7 } 'DeviceConsent')

6. Once you provide the consent, you can close the web page and go back to the backup script. There you will be prompted to choose whether to save the authorization token so that you do not have to go through the authorization process when the script runs next time. If you choose to store the authorization token, the script will not ask for authorization when it runs again.
  
\`\`\`bash
PS /var/opt/backup> ./BackupSnowflake-Linux.ps1
PowerShell Version is: 7.2
Using config file: /var/opt/backup/BackupConfig.ini
Can not find stored credential, will have to perform Device Code authentication.

To sign in, use a web browser to open the page https://us.ondatahub.com/device and enter the code 491065954 to authenticate.
Do you wish to store the token for reuse? [y/n]: y
\`\`\`  
`
  
export default content;
